import { useRecoilValue } from "recoil";
import { ActionsCell } from "../../../Component/Common/Components/DataTable/ActionsCell";
import { ActionsCellWithBadge } from "../../../Component/Common/Components/DataTable/ActionsCellWithBadge";
import { DefaultCell } from "../../../Component/Common/Components/DataTable/DefaultCell";
import { EE123PeopleRole_Edit } from "../../../Component/Common/Constants/UserRoles";
import { Individual } from "../../../Component/Model/Dto/Individual";
import { ActionType, TableColumn } from "../../../Component/Model/Table/Table";
import { userRolesState } from "../../../State/user.Atom";

interface EmployeeStructureRow {
    Id: number;
    IdentificationNumber: string;
    EmployeeCode: string;
    FullName: string;
    Race: string;
    Gender: string;
    Disabled: string;
    OccupationLevel: string;
    Position: string;
    IsYouth: string;
    Province: string;
    SupportingDocuments: any;
    Actions: ActionType[];
}

export interface EmployeeStructureTable {
    columns: Array<TableColumn>;
    rows: Array<EmployeeStructureRow>;
}

export function EmployeeStructureWithActionsTableMapper(dataObject: Individual.SummaryDtoIn[], handleEdit: (id: number) => void, handleDelete: (id: number) => void, handleAttachedSD: (id: number) => void, handleToggleDocumentPack: (id: number) => void): EmployeeStructureTable {
    const userRoles = useRecoilValue(userRolesState);

    const columns = [
        { Header: 'ID Number', accessor: 'IdentificationNumber', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Employee Code', accessor: 'EmployeeCode', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Full Name', accessor: 'FullName', Cell: ({ value }: any) => <DefaultCell value={value} tooltip={true} /> },
        { Header: 'Race', accessor: 'Race', id: 'RaceName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Gender', accessor: 'Gender', id: 'GenderId', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Disabled', accessor: 'Disabled', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Occupational Level', accessor: 'OccupationLevel', id: 'OccupationalLevelName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Position', accessor: 'Position', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Youth', accessor: 'IsYouth', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Province', accessor: 'Province', id: 'ProvinceName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Supporting Documents', accessor: 'SupportingDocuments', Cell: ({ value }: any) => <ActionsCellWithBadge actions={value} />, disableSortBy: true },
        { Header: 'Actions', accessor: 'Actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true },

    ];

    function getActionColumns(item: Individual.SummaryDtoIn): ActionType[] {
        const actions: ActionType[] = [];
        if (userRoles.includes(EE123PeopleRole_Edit)) {
            actions.push(
                {
                    label: 'View/Edit',
                    action: () => { handleEdit(item.IndividualId) },
                    key: item.IndividualId.toString(),
                    icon: { color: 'info', name: 'edit' }
                },
                {
                    label: 'Delete',
                    action: () => { handleDelete(item.IndividualId) },
                    key: item.IndividualId.toString(),
                    icon: { color: 'error', name: 'delete' }
                }
            )
        } else {
            actions.push(
                {
                    label: 'View',
                    action: () => { handleEdit(item.IndividualId) },
                    key: item.IndividualId.toString(),
                    icon: { color: 'info', name: 'visibility' }
                }
            )
        }
        return actions;
    }

    if (dataObject !== undefined && dataObject.length > 0) {
        const rows = dataObject.map((item: Individual.SummaryDtoIn) => ({
            Id: item.IndividualId,
            IdentificationNumber: item.IdentificationNumber,
            EmployeeCode: item.EmployeeCode,
            FullName: item.FullName,
            Race: item.Race,
            Gender: item.Gender,
            Disabled: item.Disabled ? 'Yes' : 'No',
            OccupationLevel: item.OccupationalLevel,
            Position: item.Position,
            IsYouth: item.IsYouth ? 'Yes' : 'No',
            Province: item.ProvinceName,
            SupportingDocuments: item.DocumentsSelectedForDownload ? [
                { label: `${item.DocumentCount} Supporting Document(s)`, action: () => { handleAttachedSD(item.IndividualId) }, key: item.IndividualId.toString(), icon: { color: 'info', name: 'attach_file' }, badgeCount: item.DocumentCount },
                // { label: 'Remove Documents from Download Pack', action: () => {handleToggleDocumentPack(item.IndividualId)}, key: item.IndividualId.toString(), icon: { color: 'secondary', name: 'bookmark_remove' } }
            ] :
                [
                    { label: `${item.DocumentCount} Supporting Document(s)`, action: () => { handleAttachedSD(item.IndividualId) }, key: item.IndividualId.toString(), icon: { color: 'info', name: 'attach_file' }, badgeCount: item.DocumentCount },
                    // { label: 'Add Documents to Download Pack', action: () => {handleToggleDocumentPack(item.IndividualId)}, key: item.IndividualId.toString(), icon: { color: 'secondary', name: 'bookmark_add' } },
                ],
            Actions: getActionColumns(item)

        }));

        return { columns: columns, rows: rows };

    } else {
        return { columns: columns, rows: [] };
    }
}


interface EmployeeStructreConsolidation extends EmployeeStructureRow {
    SourceScorecardName: string;
}

interface EmployeeStructureConsolidationTable {
    columns: Array<TableColumn>;
    rows: Array<EmployeeStructreConsolidation>;
}

export function EmployeeStructureConsolidationWithActionsTableMapper(dataObject: Individual.SummaryDtoIn[], handleEdit: (id: number) => void, handleDelete: (id: number) => void, handleAttachedSD: (id: number) => void, handleToggleDocumentPack: (id: number) => void): EmployeeStructureConsolidationTable {
    const userRoles = useRecoilValue(userRolesState);

    const columns = [
        { Header: 'ID Number', accessor: 'IdentificationNumber', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Employee Code', accessor: 'EmployeeCode', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Full Name', accessor: 'FullName', Cell: ({ value }: any) => <DefaultCell value={value} tooltip={true} /> },
        { Header: 'Race', accessor: 'Race', id: 'RaceName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Gender', accessor: 'Gender', id: 'GenderId', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Disabled', accessor: 'Disabled', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Occupational Level', accessor: 'OccupationLevel', id: 'OccupationalLevelName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Position', accessor: 'Position', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Youth', accessor: 'IsYouth', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Province', accessor: 'Province', id: 'ProvinceName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Supporting Documents', accessor: 'SupportingDocuments', Cell: ({ value }: any) => <ActionsCellWithBadge actions={value} />, disableSortBy: true },
        { Header: 'Source Scorecard', accessor: 'SourceScorecardName', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Actions', accessor: 'Actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true },
    ];

    function getActionColumns(item: Individual.SummaryDtoIn): ActionType[] {
        const actions: ActionType[] = [];
        if (userRoles.includes(EE123PeopleRole_Edit)) {
            actions.push(
                {
                    label: 'View/Edit',
                    action: () => { handleEdit(item.IndividualId) },
                    key: item.IndividualId.toString(),
                    icon: { color: 'info', name: 'edit' }
                },
                {
                    label: 'Delete',
                    action: () => { handleDelete(item.IndividualId) },
                    key: item.IndividualId.toString(),
                    icon: { color: 'error', name: 'delete' }
                }
            )
        } else {
            actions.push(
                {
                    label: 'View',
                    action: () => { handleEdit(item.IndividualId) },
                    key: item.IndividualId.toString(),
                    icon: { color: 'info', name: 'visibility' }
                }
            )
        }
        return actions;
    }

    if (dataObject !== undefined && dataObject.length > 0) {
        const rows = dataObject.map((item: Individual.SummaryDtoIn) => ({
            Id: item.IndividualId,
            IdentificationNumber: item.IdentificationNumber,
            EmployeeCode: item.EmployeeCode,
            FullName: item.FullName,
            Race: item.Race,
            Gender: item.Gender,
            Disabled: item.Disabled ? 'Yes' : 'No',
            OccupationLevel: item.OccupationalLevel,
            Position: item.Position,
            IsYouth: item.IsYouth ? 'Yes' : 'No',
            Province: item.ProvinceName,
            SupportingDocuments: item.DocumentsSelectedForDownload ? [
                { label: `${item.DocumentCount} Supporting Document(s)`, action: () => { handleAttachedSD(item.IndividualId) }, key: item.IndividualId.toString(), icon: { color: 'info', name: 'attach_file' }, badgeCount: item.DocumentCount },
                // { label: 'Remove Documents from Download Pack', action: () => {handleToggleDocumentPack(item.IndividualId)}, key: item.IndividualId.toString(), icon: { color: 'secondary', name: 'bookmark_remove' } }
            ] :
                [
                    { label: `${item.DocumentCount} Supporting Document(s)`, action: () => { handleAttachedSD(item.IndividualId) }, key: item.IndividualId.toString(), icon: { color: 'info', name: 'attach_file' }, badgeCount: item.DocumentCount },
                    // { label: 'Add Documents to Download Pack', action: () => {handleToggleDocumentPack(item.IndividualId)}, key: item.IndividualId.toString(), icon: { color: 'secondary', name: 'bookmark_add' } },
                ],
            SourceScorecardName: item.SourceScorecardName,
            Actions: getActionColumns(item)

        }));

        return { columns: columns, rows: rows };

    } else {
        return { columns: columns, rows: [] };
    }
}